/**
 * PostHog analytics wrapper.
 *
 * Initializes PostHog only when VITE_PUBLIC_POSTHOG_KEY is set.
 * In development (or when the key is absent), all calls are no-ops.
 */

import posthog from 'posthog-js';

const POSTHOG_KEY = import.meta.env.VITE_PUBLIC_POSTHOG_KEY as string | undefined;
const POSTHOG_HOST = (import.meta.env.VITE_PUBLIC_POSTHOG_HOST as string | undefined) ?? 'https://us.i.posthog.com';

let initialized = false;

export function initAnalytics(): void {
    if (!POSTHOG_KEY || initialized) return;

    posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        autocapture: true,
        capture_pageview: true,
        capture_pageleave: true,
        persistence: 'localStorage+cookie',
    });

    initialized = true;
}

// ── Event helpers ──────────────────────────────────────────────────────

export function trackPuzzleStarted(date: string, attacker: 'w' | 'b', totalSteps: number): void {
    posthog.capture('puzzle_started', { date, attacker, total_steps: totalSteps });
}

export function trackAttemptMade(date: string, moveUci: string, attemptsUsed: number, stepIndex: number, correct: boolean): void {
    posthog.capture('attempt_made', { date, move_uci: moveUci, attempts_used: attemptsUsed, step_index: stepIndex, correct });
}

export function trackPuzzleSolved(date: string, attemptsUsed: number, totalSteps: number, solveTimeSeconds?: number): void {
    posthog.capture('puzzle_solved', {
        date,
        attempts_used: attemptsUsed,
        total_steps: totalSteps,
        ...(solveTimeSeconds !== undefined && { solve_time_seconds: solveTimeSeconds }),
    });
}

export function trackPuzzleMissed(date: string, attemptsUsed: number, stepIndex: number, totalSteps: number): void {
    posthog.capture('puzzle_missed', { date, attempts_used: attemptsUsed, step_index: stepIndex, total_steps: totalSteps });
}

export function trackSolutionRevealed(date: string): void {
    posthog.capture('solution_revealed', { date });
}

export function trackShareClicked(date: string, method: 'native' | 'clipboard'): void {
    posthog.capture('share_clicked', { date, method });
}
